import React from 'react';
import { GlobeIcon, ZapIcon, RefreshCwIcon, MapPinIcon, ShieldIcon, LeafIcon, ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
import { getHotmartUrl, handleCheckoutClick } from '../utils/utmParams';
export function HowItWorks() {
  return <div data-id="element-227">
      {/* ═══════════════════════════════════════════ */}
      {/* BLOCK 1 — EYEBROW + HEADLINE               */}
      {/* ═══════════════════════════════════════════ */}
      <section className="bg-cream py-16 md:py-24 px-5" data-id="element-228">
        <div className="max-w-[1100px] mx-auto text-center" data-id="element-229">
          <p className="font-sans text-sm font-semibold uppercase tracking-widest text-forest mb-4" data-id="element-230">
            The Science Behind The System
          </p>
          <h2 className="font-serif text-forest text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6 max-w-3xl mx-auto" data-id="element-231">
            Why Your Garden Keeps Dying — And Why It's Not Your Fault
          </h2>
          <p className="font-sans text-gray-500 text-base md:text-lg leading-relaxed max-w-xl mx-auto" data-id="element-232">
            Most gardeners follow generic advice from the internet. The problem?
            That advice wasn't written for YOUR zip code, YOUR climate zone, or
            YOUR season. Here's the real reason plants die — and the system that
            fixes it.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* BLOCK 2 — THE PROBLEM EXPLAINED             */}
      {/* ═══════════════════════════════════════════ */}
      <section className="bg-white py-16 md:py-24 px-5" data-id="element-233">
        <div className="max-w-[1100px] mx-auto" data-id="element-234">
          <h3 className="font-serif text-forest text-2xl md:text-3xl font-bold mb-12 text-center md:text-left" data-id="element-235">
            The Real Reason Plants Die{' '}
            <span className="text-gray-400 font-normal" data-id="element-236">
              (It's Not What You Think)
            </span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-237">
            {/* Column 1 */}
            <div className="text-center md:text-left" data-id="element-238">
              <div className="w-14 h-14 rounded-2xl bg-forest/10 flex items-center justify-center mx-auto md:mx-0 mb-5" data-id="element-239">
                <GlobeIcon className="w-7 h-7 text-forest" strokeWidth={1.5} data-id="element-240" />
              </div>
              <h4 className="font-sans font-bold text-forest-dark text-lg mb-3" data-id="element-241">
                Generic Advice Ignores Your Zone
              </h4>
              <p className="font-sans text-gray-500 leading-relaxed text-[15px]" data-id="element-242">
                A watering tip written for Florida will kill a garden in
                Minnesota. The USA has 13 distinct climate zones — and most
                gardening advice pretends they don't exist.
              </p>
            </div>

            {/* Column 2 — KEY PROBLEM (orange accent) */}
            <div className="text-center md:text-left" data-id="element-243">
              <div className="w-14 h-14 rounded-2xl bg-burnt/10 flex items-center justify-center mx-auto md:mx-0 mb-5" data-id="element-244">
                <ZapIcon className="w-7 h-7 text-burnt" strokeWidth={1.5} data-id="element-245" />
              </div>
              <h4 className="font-sans font-bold text-burnt text-lg mb-3" data-id="element-246">
                Extreme Weather Hits Faster Than You React
              </h4>
              <p className="font-sans text-gray-500 leading-relaxed text-[15px]" data-id="element-247">
                Frost doesn't schedule appointments. A heat wave doesn't send
                reminders. By the time you Google "how to save my plants," it's
                already too late. The damage happens in the first 6 hours.
              </p>
            </div>

            {/* Column 3 */}
            <div className="text-center md:text-left" data-id="element-248">
              <div className="w-14 h-14 rounded-2xl bg-forest/10 flex items-center justify-center mx-auto md:mx-0 mb-5" data-id="element-249">
                <RefreshCwIcon className="w-7 h-7 text-forest" strokeWidth={1.5} data-id="element-250" />
              </div>
              <h4 className="font-sans font-bold text-forest-dark text-lg mb-3" data-id="element-251">
                You Repeat the Same Cycle Every Year
              </h4>
              <p className="font-sans text-gray-500 leading-relaxed text-[15px]" data-id="element-252">
                Plant in spring. Hope for the best. Lose plants in summer heat
                or fall frost. Replace in spring. Spend more money. Without a
                system, this cycle never breaks.
              </p>
            </div>
          </div>

          {/* Divider with centered text */}
          <div className="border-t border-gray-200 pt-6" data-id="element-253">
            <p className="text-center font-serif italic text-burnt text-base md:text-lg max-w-xl mx-auto" data-id="element-254">
              "The problem isn't your green thumb — it's the missing system
              between YOU and your climate."
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* BLOCK 3 — THE MECHANISM REVEAL              */}
      {/* ═══════════════════════════════════════════ */}
      <section className="py-16 md:py-24 px-5" data-id="element-255">
        <div className="max-w-[1100px] mx-auto" data-id="element-256">
          <div className="bg-[#D8F3DC] rounded-2xl md:rounded-3xl py-14 md:py-20 px-6 md:px-12" data-id="element-257">
            <div className="text-center mb-14" data-id="element-258">
              <p className="font-sans text-sm font-semibold uppercase tracking-widest text-burnt mb-4" data-id="element-259">
                Introducing The Mechanism
              </p>
              <h3 className="font-serif text-forest text-3xl md:text-4xl font-bold mb-4" data-id="element-260">
                The Hardiness Shield System™
              </h3>
              <p className="font-sans text-gray-600 max-w-2xl mx-auto leading-relaxed" data-id="element-261">
                A 3-layer protection protocol built around the official USDA
                Plant Hardiness Zone Map — the same system professional
                landscapers use, simplified into a single actionable guide.
              </p>
            </div>

            {/* Step-by-step flow */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 items-start relative" data-id="element-262">
              {/* Step 1 */}
              <div className="bg-white rounded-xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.08)] text-center relative z-10" data-id="element-263">
                <div className="w-12 h-12 rounded-full bg-forest flex items-center justify-center mx-auto mb-4" data-id="element-264">
                  <MapPinIcon className="w-6 h-6 text-white" data-id="element-265" />
                </div>
                <p className="font-sans text-xs font-bold uppercase tracking-widest text-burnt mb-2" data-id="element-266">
                  Step 1
                </p>
                <h4 className="font-sans font-bold text-forest-dark text-lg mb-2" data-id="element-267">
                  Identify Your Zone
                </h4>
                <p className="font-sans text-gray-500 text-sm leading-relaxed" data-id="element-268">
                  Find your exact USDA Hardiness Zone using our included map.
                  Your zone determines your frost dates, heat index, and plant
                  survival thresholds.
                </p>
              </div>

              {/* Arrow 1 (hidden on mobile) */}
              <div className="hidden md:flex absolute left-[33%] top-1/2 -translate-y-1/2 -translate-x-1/2 z-20" data-id="element-269">
                <div className="w-8 h-8 rounded-full bg-forest/20 flex items-center justify-center" data-id="element-270">
                  <ArrowRightIcon className="w-4 h-4 text-forest" data-id="element-271" />
                </div>
              </div>
              {/* Arrow mobile */}
              <div className="flex md:hidden justify-center -my-2" data-id="element-272">
                <div className="w-8 h-8 rounded-full bg-forest/20 flex items-center justify-center rotate-90" data-id="element-273">
                  <ArrowRightIcon className="w-4 h-4 text-forest" data-id="element-274" />
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-white rounded-xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.08)] text-center relative z-10" data-id="element-275">
                <div className="w-12 h-12 rounded-full bg-burnt flex items-center justify-center mx-auto mb-4" data-id="element-276">
                  <ShieldIcon className="w-6 h-6 text-white" data-id="element-277" />
                </div>
                <p className="font-sans text-xs font-bold uppercase tracking-widest text-burnt mb-2" data-id="element-278">
                  Step 2
                </p>
                <h4 className="font-sans font-bold text-forest-dark text-lg mb-2" data-id="element-279">
                  Activate The Seasonal Protocol
                </h4>
                <p className="font-sans text-gray-500 text-sm leading-relaxed" data-id="element-280">
                  Each season has a specific protection checklist. You follow
                  the right protocol for YOUR zone at the RIGHT time — before
                  the weather hits, not after.
                </p>
              </div>

              {/* Arrow 2 (hidden on mobile) */}
              <div className="hidden md:flex absolute left-[67%] top-1/2 -translate-y-1/2 -translate-x-1/2 z-20" data-id="element-281">
                <div className="w-8 h-8 rounded-full bg-forest/20 flex items-center justify-center" data-id="element-282">
                  <ArrowRightIcon className="w-4 h-4 text-forest" data-id="element-283" />
                </div>
              </div>
              {/* Arrow mobile */}
              <div className="flex md:hidden justify-center -my-2" data-id="element-284">
                <div className="w-8 h-8 rounded-full bg-forest/20 flex items-center justify-center rotate-90" data-id="element-285">
                  <ArrowRightIcon className="w-4 h-4 text-forest" data-id="element-286" />
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-white rounded-xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.08)] text-center relative z-10" data-id="element-287">
                <div className="w-12 h-12 rounded-full bg-forest flex items-center justify-center mx-auto mb-4" data-id="element-288">
                  <LeafIcon className="w-6 h-6 text-white" data-id="element-289" />
                </div>
                <p className="font-sans text-xs font-bold uppercase tracking-widest text-burnt mb-2" data-id="element-290">
                  Step 3
                </p>
                <h4 className="font-sans font-bold text-forest-dark text-lg mb-2" data-id="element-291">
                  Your Garden Survives & Thrives
                </h4>
                <p className="font-sans text-gray-500 text-sm leading-relaxed" data-id="element-292">
                  With the right preparation in place, your plants are protected
                  from frost, heat waves, storms and flooding — season after
                  season, year after year.
                </p>
              </div>
            </div>

            {/* Badge */}
            <div className="text-center mt-10" data-id="element-293">
              <span className="inline-flex items-center gap-2 bg-forest text-white font-sans text-sm font-semibold px-5 py-2.5 rounded-full shadow-md" data-id="element-294">
                <CheckCircleIcon className="w-4 h-4" data-id="element-295" />
                Used by 11,000+ American homeowners
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* BLOCK 4 — FOUR SEASONS BREAKDOWN            */}
      {/* ═══════════════════════════════════════════ */}
      <section className="bg-cream py-16 md:py-24 px-5" data-id="element-296">
        <div className="max-w-[1100px] mx-auto" data-id="element-297">
          <h3 className="font-serif text-forest text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-3" data-id="element-298">
            One System. Four Seasons. Zero Dead Plants.
          </h3>
          <p className="font-sans text-gray-500 text-center mb-12 max-w-xl mx-auto" data-id="element-299">
            Each season has a dedicated protection protocol inside The 365
            Garden Shield:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-300">
            {/* Winter */}
            <div className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] overflow-hidden" data-id="element-301">
              <div className="h-1.5 bg-[#AED9E0]" data-id="element-302" />
              <div className="p-6" data-id="element-303">
                <span className="text-4xl block mb-3" data-id="element-304">❄️</span>
                <h4 className="font-sans font-bold text-forest-dark text-sm uppercase tracking-wider mb-4" data-id="element-305">
                  Winter Protocol
                </h4>
                <ul className="space-y-2.5" data-id="element-306">
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-307">
                    <span className="text-[#AED9E0] mt-0.5" data-id="element-308">•</span>
                    Mulching roots before the deep freeze
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-309">
                    <span className="text-[#AED9E0] mt-0.5" data-id="element-310">•</span>
                    Identifying cold-hardy vs. vulnerable plants
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-311">
                    <span className="text-[#AED9E0] mt-0.5" data-id="element-312">•</span>
                    Emergency steps when temps drop below 20°F
                  </li>
                </ul>
              </div>
            </div>

            {/* Spring */}
            <div className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] overflow-hidden" data-id="element-313">
              <div className="h-1.5 bg-[#FFB7C5]" data-id="element-314" />
              <div className="p-6" data-id="element-315">
                <span className="text-4xl block mb-3" data-id="element-316">🌸</span>
                <h4 className="font-sans font-bold text-forest-dark text-sm uppercase tracking-wider mb-4" data-id="element-317">
                  Spring Protocol
                </h4>
                <ul className="space-y-2.5" data-id="element-318">
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-319">
                    <span className="text-[#FFB7C5] mt-0.5" data-id="element-320">•</span>
                    Safe transplanting windows by zone
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-321">
                    <span className="text-[#FFB7C5] mt-0.5" data-id="element-322">•</span>
                    Protecting new growth from late frosts
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-323">
                    <span className="text-[#FFB7C5] mt-0.5" data-id="element-324">•</span>
                    Soil preparation after snow melt
                  </li>
                </ul>
              </div>
            </div>

            {/* Summer */}
            <div className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] overflow-hidden" data-id="element-325">
              <div className="h-1.5 bg-[#FFD166]" data-id="element-326" />
              <div className="p-6" data-id="element-327">
                <span className="text-4xl block mb-3" data-id="element-328">☀️</span>
                <h4 className="font-sans font-bold text-forest-dark text-sm uppercase tracking-wider mb-4" data-id="element-329">
                  Summer Protocol
                </h4>
                <ul className="space-y-2.5" data-id="element-330">
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-331">
                    <span className="text-[#FFD166] mt-0.5" data-id="element-332">•</span>
                    Deep hydration timing for 90°F+ days
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-333">
                    <span className="text-[#FFD166] mt-0.5" data-id="element-334">•</span>
                    Shade strategies using the Cloud Cover Hack
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-335">
                    <span className="text-[#FFD166] mt-0.5" data-id="element-336">•</span>
                    Signs of heat stress before it's too late
                  </li>
                </ul>
              </div>
            </div>

            {/* Fall */}
            <div className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] overflow-hidden" data-id="element-337">
              <div className="h-1.5 bg-burnt" data-id="element-338" />
              <div className="p-6" data-id="element-339">
                <span className="text-4xl block mb-3" data-id="element-340">🍂</span>
                <h4 className="font-sans font-bold text-forest-dark text-sm uppercase tracking-wider mb-4" data-id="element-341">
                  Fall Protocol
                </h4>
                <ul className="space-y-2.5" data-id="element-342">
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-343">
                    <span className="text-burnt mt-0.5" data-id="element-344">•</span>
                    Pre-frost checklist (The 10 PM Action Plan)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-345">
                    <span className="text-burnt mt-0.5" data-id="element-346">•</span>
                    Bulb storage and root protection
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-347">
                    <span className="text-burnt mt-0.5" data-id="element-348">•</span>
                    Storm-proofing before winter winds arrive
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* BLOCK 5 — WITHOUT VS WITH COMPARISON        */}
      {/* ═══════════════════════════════════════════ */}
      <section className="bg-white py-16 md:py-24 px-5" data-id="element-349">
        <div className="max-w-[1100px] mx-auto" data-id="element-350">
          <h3 className="font-serif text-forest text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-12" data-id="element-351">
            Two Gardeners. Same Storm. Very Different Results.
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-0 max-w-4xl mx-auto relative" data-id="element-352">
            {/* VS Badge */}
            <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20" data-id="element-353">
              <div className="w-14 h-14 rounded-full bg-forest shadow-xl flex items-center justify-center border-4 border-white" data-id="element-354">
                <span className="font-sans font-bold text-white text-sm" data-id="element-355">
                  VS
                </span>
              </div>
            </div>

            {/* WITHOUT */}
            <div className="rounded-xl md:rounded-r-none overflow-hidden border border-red-200" data-id="element-356">
              <div className="bg-red-50 px-6 py-4" data-id="element-357">
                <h4 className="font-sans font-bold text-red-600 text-lg" data-id="element-358">
                  ❌ Without The System
                </h4>
              </div>
              <div className="px-6 py-5 space-y-3.5 bg-white" data-id="element-359">
                <div className="flex items-start gap-2.5" data-id="element-360">
                  <span className="text-red-400 font-bold mt-0.5 flex-shrink-0" data-id="element-361">
                    ✗
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-362">
                    Checks the weather after damage is done
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-363">
                  <span className="text-red-400 font-bold mt-0.5 flex-shrink-0" data-id="element-364">
                    ✗
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-365">
                    Follows random internet tips not built for their zone
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-366">
                  <span className="text-red-400 font-bold mt-0.5 flex-shrink-0" data-id="element-367">
                    ✗
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-368">
                    Spends $200–$500 replacing dead plants every year
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-369">
                  <span className="text-red-400 font-bold mt-0.5 flex-shrink-0" data-id="element-370">
                    ✗
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-371">
                    Panics when a freeze warning hits at 9 PM
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-372">
                  <span className="text-red-400 font-bold mt-0.5 flex-shrink-0" data-id="element-373">
                    ✗
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-374">
                    Has a "hope for the best" strategy
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-375">
                  <span className="text-red-400 font-bold mt-0.5 flex-shrink-0" data-id="element-376">
                    ✗
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-377">
                    Repeats the same mistakes every season
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile VS */}
            <div className="flex md:hidden justify-center -my-2" data-id="element-378">
              <div className="w-12 h-12 rounded-full bg-forest shadow-lg flex items-center justify-center" data-id="element-379">
                <span className="font-sans font-bold text-white text-sm" data-id="element-380">
                  VS
                </span>
              </div>
            </div>

            {/* WITH */}
            <div className="rounded-xl md:rounded-l-none overflow-hidden border border-green-200" data-id="element-381">
              <div className="bg-[#D8F3DC] px-6 py-4" data-id="element-382">
                <h4 className="font-sans font-bold text-forest text-lg" data-id="element-383">
                  ✅ With The 365 Garden Shield
                </h4>
              </div>
              <div className="px-6 py-5 space-y-3.5 bg-white" data-id="element-384">
                <div className="flex items-start gap-2.5" data-id="element-385">
                  <span className="text-forest font-bold mt-0.5 flex-shrink-0" data-id="element-386">
                    ✓
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-387">
                    Checks their seasonal protocol before weather hits
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-388">
                  <span className="text-forest font-bold mt-0.5 flex-shrink-0" data-id="element-389">
                    ✓
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-390">
                    Follows zone-specific steps for their exact climate
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-391">
                  <span className="text-forest font-bold mt-0.5 flex-shrink-0" data-id="element-392">
                    ✓
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-393">
                    Saves their entire plant investment year after year
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-394">
                  <span className="text-forest font-bold mt-0.5 flex-shrink-0" data-id="element-395">
                    ✓
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-396">
                    Has a 10 PM Frost Checklist ready to execute
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-397">
                  <span className="text-forest font-bold mt-0.5 flex-shrink-0" data-id="element-398">
                    ✓
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-399">
                    Has a clear, confident plan for every season
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-400">
                  <span className="text-forest font-bold mt-0.5 flex-shrink-0" data-id="element-401">
                    ✓
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-402">
                    Breaks the "plant and replace" cycle for good
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* CTA below comparison */}
          <div className="text-center mt-12" data-id="element-403">
            <a href={getHotmartUrl()} target="_blank" rel="noopener noreferrer" onClick={handleCheckoutClick} className="cta-heartbeat inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-lg font-bold py-4 px-10 rounded-lg shadow-lg transition-colors duration-200 mb-3" data-id="element-404">
              <span data-id="element-405">🛡️</span>
              <span data-id="element-406">GET INSTANT ACCESS — JUST $6.37</span>
            </a>
            <p className="text-sm text-gray-500 font-sans" data-id="element-407">
              Instant PDF Download · Works on any device · 7-Day Money Back
              Guarantee
            </p>
          </div>
        </div>
      </section>
    </div>;
}